/* ============================================================
   Sawt Al Zaman — custom cart drawer behavior
   - <saz-rewards>: animates progress fill, fires confetti when
     the free-shipping tier is crossed.
   - <saz-cart-upsell>: fetches related products, renders cards,
     inline variant picker, adds to cart via Section Rendering.
   Elements re-instantiate on every drawer re-render (innerHTML
   replacement), so state lives in sessionStorage / closures.
   ============================================================ */
(function () {
  'use strict';

  if (customElements.get('saz-rewards')) return;

  var BRAND_COLORS = ['#1f0d0b', '#e9c89f', '#c8944f', '#fff6e8', '#d9ab6e'];

  /* ---------- money formatting ---------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = Math.round(parseFloat(cents) * 100);
    if (isNaN(cents)) cents = 0;
    var amount = (cents / 100).toFixed(2);
    if (format && format.indexOf('{{') !== -1) {
      return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (m, key) {
        switch (key) {
          case 'amount':
            return amount;
          case 'amount_no_decimals':
            return String(Math.round(cents / 100));
          case 'amount_with_comma_separator':
            return amount.replace('.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return String(Math.round(cents / 100));
          default:
            return amount;
        }
      });
    }
    return '$' + amount;
  }

  /* ---------- confetti ---------- */
  function fireConfetti() {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var canvas = document.createElement('canvas');
    canvas.className = 'saz-confetti';
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    document.body.appendChild(canvas);
    var ctx = canvas.getContext('2d');
    var particles = [];
    var originX = Math.min(window.innerWidth - 215, window.innerWidth * 0.8);
    for (var i = 0; i < 110; i++) {
      particles.push({
        x: originX + (Math.random() - 0.5) * 260,
        y: 90 + Math.random() * 60,
        vx: (Math.random() - 0.5) * 7,
        vy: -(Math.random() * 8 + 3),
        w: 5 + Math.random() * 6,
        h: 8 + Math.random() * 6,
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.3,
        color: BRAND_COLORS[Math.floor(Math.random() * BRAND_COLORS.length)],
      });
    }
    var start = null;
    function tick(ts) {
      if (!start) start = ts;
      var elapsed = ts - start;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(function (p) {
        p.vy += 0.22;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.globalAlpha = Math.max(0, 1 - elapsed / 1900);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      });
      if (elapsed < 2000) {
        requestAnimationFrame(tick);
      } else {
        canvas.remove();
      }
    }
    requestAnimationFrame(tick);
  }

  /* ---------- rewards bar ---------- */
  var SazRewards = function () {
    return Reflect.construct(HTMLElement, [], SazRewards);
  };
  SazRewards.prototype = Object.create(HTMLElement.prototype);
  SazRewards.prototype.constructor = SazRewards;
  Object.setPrototypeOf(SazRewards, HTMLElement);

  SazRewards.prototype.connectedCallback = function () {
    var count = parseInt(this.dataset.count || '0', 10);
    var goal = parseInt(this.dataset.goal || '4', 10);
    var prevRaw = sessionStorage.getItem('sazRewardCount');
    var prev = prevRaw === null ? count : parseInt(prevRaw, 10);
    sessionStorage.setItem('sazRewardCount', String(count));

    /* animate fill from previous state (matches Liquid's piecewise 0/17/33/66/100 mapping) */
    var STOPS = [0, 17, 33, 66, 100];
    var fill = this.querySelector('.saz-rewards__fill');
    if (fill && prev !== count) {
      var target = fill.style.width;
      fill.style.transition = 'none';
      fill.style.width = STOPS[Math.max(0, Math.min(prev, goal))] + '%';
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          fill.style.transition = '';
          fill.style.width = target;
        });
      });
    }

    /* celebrate crossing the final tier */
    if (count >= goal && prev < goal) {
      setTimeout(fireConfetti, 350);
    }
  };
  customElements.define('saz-rewards', SazRewards);

  /* ---------- upsell ---------- */
  var SazCartUpsell = function () {
    return Reflect.construct(HTMLElement, [], SazCartUpsell);
  };
  SazCartUpsell.prototype = Object.create(HTMLElement.prototype);
  SazCartUpsell.prototype.constructor = SazCartUpsell;
  Object.setPrototypeOf(SazCartUpsell, HTMLElement);

  SazCartUpsell.prototype.connectedCallback = function () {
    var self = this;
    /* wait a microtask so innerHTML children are parsed when re-rendered */
    setTimeout(function () {
      self.init();
    }, 0);
  };

  SazCartUpsell.prototype.init = function () {
    var self = this;
    var exclude = (this.dataset.exclude || '').split(',').filter(Boolean);
    var seed = this.dataset.seed;
    var recUrl = this.dataset.recommendationsUrl || '/recommendations/products';

    var fromRecommendations = seed
      ? fetch(recUrl + '.json?product_id=' + seed + '&limit=8&intent=related')
          .then(function (r) {
            return r.ok ? r.json() : { products: [] };
          })
          .then(function (j) {
            return (j.products || []).map(function (p) {
              return {
                handle: p.handle,
                id: p.id,
                title: p.title,
                url: p.url,
                price: p.price,
                image: p.featured_image || (p.images && p.images[0]) || '',
              };
            });
          })
          .catch(function () {
            return [];
          })
      : Promise.resolve([]);

    fromRecommendations.then(function (products) {
      if (!products.length) {
        var fb = self.querySelector('.saz-upsell-fallback');
        if (fb) {
          try {
            products = JSON.parse(fb.textContent);
          } catch (e) {
            products = [];
          }
        }
      }
      products = products.filter(function (p) {
        return exclude.indexOf(p.handle) === -1;
      });
      /* de-dup by handle */
      var seen = {};
      products = products.filter(function (p) {
        if (seen[p.handle]) return false;
        seen[p.handle] = true;
        return true;
      });
      products = products.slice(0, 2);
      if (!products.length) return;
      self.render(products);
    });
  };

  SazCartUpsell.prototype.render = function (products) {
    var self = this;
    var inner = this.querySelector('.saz-upsell__inner');
    var list = this.querySelector('.saz-upsell__list');
    if (!inner || !list) return;
    list.innerHTML = '';
    products.forEach(function (p) {
      var card = document.createElement('div');
      card.className = 'saz-upsell__card';
      card.setAttribute('data-handle', p.handle);
      var img = p.image
        ? '<img class="saz-upsell__image" src="' + p.image + '" alt="" loading="lazy" width="80" height="80">'
        : '<span class="saz-upsell__image" aria-hidden="true"></span>';
      card.innerHTML =
        img +
        '<div class="saz-upsell__info">' +
        '<a class="saz-upsell__title" href="' + p.url + '">' + escapeHtml(p.title) + '</a>' +
        '<p class="saz-upsell__price">' + formatMoney(p.price, self.dataset.moneyFormat) + '</p>' +
        '</div>' +
        '<button type="button" class="saz-upsell__add" aria-expanded="false" aria-label="Add ' + escapeHtml(p.title) + ' to cart">+</button>' +
        '<div class="saz-upsell__picker" hidden></div>';
      list.appendChild(card);
      var btn = card.querySelector('.saz-upsell__add');
      btn.addEventListener('click', function () {
        self.onAddClick(card, p, btn);
      });
    });
    inner.hidden = false;
  };

  SazCartUpsell.prototype.onAddClick = function (card, product, btn) {
    var self = this;
    var picker = card.querySelector('.saz-upsell__picker');

    /* toggle picker if it is already built */
    if (!picker.hidden) {
      picker.hidden = true;
      btn.setAttribute('aria-expanded', 'false');
      return;
    }
    if (picker.dataset.built === 'true') {
      picker.hidden = false;
      btn.setAttribute('aria-expanded', 'true');
      return;
    }

    btn.disabled = true;
    fetch('/products/' + product.handle + '.js')
      .then(function (r) {
        return r.json();
      })
      .then(function (full) {
        btn.disabled = false;
        var available = full.variants.filter(function (v) {
          return v.available;
        });
        if (full.variants.length === 1) {
          self.addVariant(full.variants[0].id, btn, card);
          return;
        }
        if (!available.length) {
          self.showError(card, 'Sold out');
          return;
        }
        self.buildPicker(card, full, picker);
        picker.hidden = false;
        btn.setAttribute('aria-expanded', 'true');
      })
      .catch(function () {
        btn.disabled = false;
        self.showError(card, 'Could not load options');
      });
  };

  SazCartUpsell.prototype.buildPicker = function (card, full, picker) {
    var self = this;
    picker.dataset.built = 'true';
    picker.innerHTML = '';
    var selected = [];

    var firstAvailable = full.variants.filter(function (v) {
      return v.available;
    })[0];
    full.options.forEach(function (opt, i) {
      selected[i] = firstAvailable ? firstAvailable.options[i] : opt.values[0];
    });

    full.options.forEach(function (opt, optIndex) {
      var name = document.createElement('p');
      name.className = 'saz-upsell__opt-name';
      name.textContent = opt.name;
      picker.appendChild(name);
      var pills = document.createElement('div');
      pills.className = 'saz-upsell__pills';
      opt.values.forEach(function (value) {
        var pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'saz-upsell__pill';
        pill.textContent = value;
        pill.setAttribute('aria-pressed', String(selected[optIndex] === value));
        pill.addEventListener('click', function () {
          selected[optIndex] = value;
          pills.querySelectorAll('.saz-upsell__pill').forEach(function (b) {
            b.setAttribute('aria-pressed', String(b.textContent === value));
          });
          update();
        });
        pills.appendChild(pill);
      });
      picker.appendChild(pills);
    });

    var confirm = document.createElement('button');
    confirm.type = 'button';
    confirm.className = 'saz-upsell__confirm';
    picker.appendChild(confirm);

    function match() {
      return full.variants.find(function (v) {
        return v.options.every(function (o, i) {
          return o === selected[i];
        });
      });
    }
    function update() {
      var v = match();
      if (v && v.available) {
        confirm.disabled = false;
        confirm.textContent = 'Add to cart · ' + formatMoney(v.price, self.dataset.moneyFormat).replace(/<[^>]*>/g, '');
      } else {
        confirm.disabled = true;
        confirm.textContent = v ? 'Sold out' : 'Unavailable';
      }
    }
    update();

    confirm.addEventListener('click', function () {
      var v = match();
      if (!v || !v.available) return;
      confirm.disabled = true;
      confirm.textContent = 'Adding…';
      self.addVariant(v.id, confirm, card);
    });
  };

  SazCartUpsell.prototype.addVariant = function (variantId, sourceBtn, card) {
    var self = this;
    if (sourceBtn) sourceBtn.disabled = true;
    var body = new FormData();
    body.append('id', variantId);
    body.append('quantity', '1');
    body.append('sections', 'cart-drawer,cart-icon-bubble');
    body.append('sections_url', window.location.pathname);

    var addUrl = (this.dataset.cartAddUrl || '/cart/add') + '.js';
    fetch(addUrl, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: body,
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (json) {
        if (json.status && json.status !== 200) {
          if (sourceBtn) sourceBtn.disabled = false;
          self.showError(card, json.description || json.message || 'Could not add to cart');
          return;
        }
        var drawer = document.querySelector('cart-drawer');
        if (drawer && json.sections) {
          drawer.renderContents(json);
        } else {
          window.location.reload();
        }
      })
      .catch(function () {
        if (sourceBtn) sourceBtn.disabled = false;
        self.showError(card, 'Could not add to cart');
      });
  };

  SazCartUpsell.prototype.showError = function (card, message) {
    var existing = card.querySelector('.saz-upsell__error');
    if (existing) existing.remove();
    var p = document.createElement('p');
    p.className = 'saz-upsell__error';
    p.textContent = message;
    card.appendChild(p);
    setTimeout(function () {
      p.remove();
    }, 4000);
  };

  customElements.define('saz-cart-upsell', SazCartUpsell);

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
})();
